import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { FileText, Shield, Package, AlertCircle } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPermissions: 0,
    activeDocuments: 0,
    totalTanks: 0,
    expiringSoon: 0,
  });
  const [recentPermissions, setRecentPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const permissions = response.data;
      const totalPermissions = permissions.length;
      const activeDocuments = permissions.filter(p => p.has_active_document && p.document_status === 'active').length;
      const expiredDocuments = permissions.filter(p => p.has_active_document && p.document_status === 'expired').length;
      const pendingDocuments = permissions.filter(p => p.has_active_document && p.document_status === 'pending').length;

      setStats({
        totalPermissions,
        activeDocuments,
        expiredDocuments,
        pendingDocuments,
      });

      setRecentPermissions(permissions.slice(0, 5));
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error(t.sessionExpired);
        navigate('/login');
      } else {
        toast.error(t.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      titleKey: 'totalPermissions',
      value: stats.totalPermissions,
      icon: Shield,
      color: 'bg-blue-500',
      testId: 'stat-total-permissions',
    },
    {
      titleKey: 'activeDocuments',
      value: stats.activeDocuments,
      icon: FileText,
      color: 'bg-green-500',
      testId: 'stat-active-documents',
    },
    {
      titleKey: 'expiredDocuments',
      value: stats.expiredDocuments,
      icon: AlertCircle,
      color: 'bg-red-500',
      testId: 'stat-expired-documents',
    },
    {
      titleKey: 'pendingDocuments',
      value: stats.pendingDocuments,
      icon: Package,
      color: 'bg-orange-500',
      testId: 'stat-pending-documents',
    },
  ];

  return (
    <Layout>
      <div data-testid="dashboard-page" className="space-y-8">
        <div className={`flex justify-between items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className={isRtl ? 'text-right' : ''}>
            <h1 className="text-4xl font-bold text-slate-900">{t.welcomeBack}</h1>
            <p className="text-slate-600 mt-2">{t.dashboardSubtitle}</p>
          </div>
          <Button
            data-testid="dashboard-create-permission-button"
            onClick={() => navigate('/permissions')}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {t.createPermission}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">{t.loading}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card
                    key={index}
                    data-testid={stat.testId}
                    className="p-6 bg-white border border-slate-200 hover:shadow-lg transition-shadow"
                  >
                    <div className={`flex items-start justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
                      <div className={isRtl ? 'text-right' : ''}>
                        <p className="text-sm font-medium text-slate-600">{t[stat.titleKey]}</p>
                        <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                      </div>
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <Card className="p-6 bg-white border border-slate-200">
              <div className={`flex justify-between items-center mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <h2 className="text-2xl font-bold text-slate-900">{t.recentPermissions}</h2>
                <Button
                  variant="outline"
                  onClick={() => navigate('/permissions')}
                  className="text-sm"
                >
                  {t.viewAll}
                </Button>
              </div>
              {recentPermissions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500">{t.noPermissions}</p>
                  <Button
                    data-testid="dashboard-empty-create-button"
                    onClick={() => navigate('/permissions')}
                    className="mt-4 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {t.createPermission}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentPermissions.map((permission) => (
                    <div
                      key={permission.permission_number}
                      data-testid={`permission-item-${permission.permission_number}`}
                      className={`flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer ${isRtl ? 'flex-row-reverse' : ''}`}
                      onClick={() => navigate(`/permissions/${permission.permission_number}`)}
                    >
                      <div className={`flex items-center gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <div className="bg-slate-900 text-white rounded-lg px-4 py-2 font-mono font-bold">
                          #{permission.permission_number}
                        </div>
                        <div className={isRtl ? 'text-right' : ''}>
                          <p className="font-medium text-slate-900">
                            {isRtl ? `${permission.permission_number}# ${t.permissions}` : `Permission #${permission.permission_number}`}
                          </p>
                          <p className="text-sm text-slate-600">
                            {permission.tank_ids.length} {t.tanks}
                          </p>
                        </div>
                      </div>
                      <div>
                        {permission.document_status && (
                          <span className={`status-badge ${permission.document_status}`}>
                            {t[permission.document_status] || permission.document_status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
